import { useTranslation } from 'react-i18next'
import { useEffect, useRef, useState, type ReactNode } from 'react'
import { ChevronDown, Check } from 'lucide-react'
import { cn } from '../../lib/cn'

export interface SelectOption {
  value: string
  label: string
  /** Optional secondary line — HTML allowed (matches the API's rich descriptions). */
  description?: string
}

/** In-app styled dropdown (replaces the native <select>). */
export function Select({
  value,
  options,
  onChange,
  placeholder = '—',
  icon,
  openUp = false,
  disabled,
  className,
  ariaLabel,
  searchable = false,
  searchPlaceholder,
}: {
  value: string
  options: SelectOption[]
  onChange: (v: string) => void
  placeholder?: string
  icon?: ReactNode
  /** Open above the trigger (use when the field sits near the bottom action bar). */
  openUp?: boolean
  disabled?: boolean
  className?: string
  ariaLabel?: string
  /** Show a filter box at the top of the list (use for long lists like countries). */
  searchable?: boolean
  searchPlaceholder?: string
}) {
  const { t } = useTranslation()
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState('')
  const ref = useRef<HTMLDivElement>(null)

  useEffect(() => {
    function onDoc(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false)
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') setOpen(false)
    }
    if (open) {
      document.addEventListener('mousedown', onDoc)
      document.addEventListener('keydown', onKey)
    }
    return () => {
      document.removeEventListener('mousedown', onDoc)
      document.removeEventListener('keydown', onKey)
    }
  }, [open])

  useEffect(() => {
    if (!open) setQuery('')
  }, [open])

  const current = options.find((o) => o.value === value)
  const q = query.trim().toLowerCase()
  const shown = searchable && q ? options.filter((o) => o.label.toLowerCase().includes(q)) : options

  return (
    <div ref={ref} className={cn('relative', className)}>
      <button
        type="button"
        aria-label={ariaLabel}
        aria-haspopup="listbox"
        aria-expanded={open}
        disabled={disabled}
        onClick={() => setOpen((o) => !o)}
        className="gsm-input flex items-center justify-between gap-2 disabled:cursor-not-allowed disabled:opacity-50"
      >
        <span className="flex min-w-0 items-center gap-2 font-medium">
          {icon}
          <span className="truncate">{current ? current.label : placeholder}</span>
        </span>
        <ChevronDown className={cn('h-4 w-4 flex-none text-ink-400 transition-transform', open && 'rotate-180')} />
      </button>
      {open && (
        <ul
          role="listbox"
          className={cn(
            'absolute left-0 z-30 max-h-64 w-full animate-fade-up overflow-auto rounded-xl border border-ink-100 bg-white p-1 shadow-pop',
            openUp ? 'bottom-full mb-2' : 'top-full mt-2',
          )}
        >
          {searchable && (
            <li className="sticky top-0 z-10 -mx-1 -mt-1 mb-1 border-b border-ink-100 bg-white px-2 pb-2 pt-1.5">
              <input
                autoFocus
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder={searchPlaceholder ?? t('common.search')}
                className="w-full rounded-lg border border-ink-200 bg-white px-3 py-1.5 text-[14px] text-ink-900 outline-none placeholder:text-ink-300 focus:border-brand-500"
              />
            </li>
          )}
          {shown.length === 0 && (
            <li className="px-3 py-4 text-center text-[14px] text-ink-400">{t('common.noResults')}</li>
          )}
          {shown.map((o) => {
            const sel = o.value === value
            return (
              <li key={o.value}>
                <button
                  type="button"
                  role="option"
                  aria-selected={sel}
                  onClick={() => {
                    onChange(o.value)
                    setOpen(false)
                  }}
                  className={cn(
                    'flex w-full items-start justify-between gap-2 rounded-lg px-3 py-2 text-left text-[15px] transition-colors',
                    sel ? 'bg-brand-50 text-brand-700' : 'text-ink-700 hover:bg-ink-50',
                  )}
                >
                  <span className="min-w-0">
                    <span className={cn('block truncate', sel && 'font-semibold')}>{o.label}</span>
                    {o.description && (
                      <span
                        className="mt-0.5 block text-[12px] leading-snug text-ink-400 [&_p]:m-0"
                        dangerouslySetInnerHTML={{ __html: o.description }}
                      />
                    )}
                  </span>
                  {sel && <Check className="mt-0.5 h-4 w-4 flex-none text-brand-600" />}
                </button>
              </li>
            )
          })}
        </ul>
      )}
    </div>
  )
}
